import { Injectable } from '@angular/core';
import { Item, KPI, RouteInfo, RouteInfoAPIResponse, RouteInfoResp, Routes, Stop, Task } from '../../models/kpi-details';
import { DataRepositoryService } from '../data-repository/data-repository.service';
import { DataFormatService } from './data-format.service';
import { RouteState } from '../../entity/enums';

@Injectable({
    providedIn: 'root'
})
export class ResponseDataFormatService {

    constructor(
        private dataRepositoryService: DataRepositoryService
        ,private dataFormatService: DataFormatService
    ) { }

    resModelFormatter(routeInfoRes: RouteInfoAPIResponse): RouteInfoAPIResponse {
        let routes: RouteInfoAPIResponse;

        let routeInfo = this.getRouteInfo(routeInfoRes);
        routes = {
            etag: routeInfoRes?.etag ?? 0,
            route_info: routeInfo
        }

        return routes;
    }

    getRouteInfo(routeInfoList: RouteInfoResp): RouteInfo[] {
        let routeInfo: RouteInfo[] = [];
        routeInfoList.route_info.forEach(rI => {
            routeInfo.push({
                business_location_id: rI.business_location_id ?? '',
                business_location_name: this.dataRepositoryService.businessLocationListMap.get(rI.business_location_id)?.name??'',
                routes: this.getRoutes(rI.routes)
            })

        })
        routeInfo = this.sort(routeInfo, 'business_location_name');
        return routeInfo;
    }

    getRoutes(routeList: Routes[]): Routes[] {
        let routes: Routes[] = [] as Routes[];
        routeList.forEach((route: Routes) => {
            let routeObj: Routes = {
                kpi_extended:route.kpi_extended,
                kpi: this.getKPI(route),
                route_id: route.route_id,
                route_label: route.route_label,
                route_schedule: route.route_schedule,
                route_state: route.route_state,
                route_time_window: route.route_time_window,
                failed_tasks: route.failed_tasks,
                workshift_name: route?.workshift_name??'',
                vehicle: route.vehicle??'',
                driver_information:route?.driver_information,
                break_time_window:route?.break_time_window,
                finishloading_ts: route?.finishloading_ts,
                loading_ts: route?.loading_ts,
                onroute_ts: route?.onroute_ts,
                canCompleteRoute: this.canMarkRouteComplete(route),
                container_information:route?.container_information??{},
                enable_slotin: route?.enable_slotin??false
            }
            routes.push(routeObj)
        })
        routes = this.sort(routes, 'route_label');
        routes.forEach(route=>{
            this.dataRepositoryService.kpiDataMap.set(route.route_id, route);
        })
        return routes;
    }


    getKPI(route: Routes): KPI {
        return {
            items: this.getItems(route.kpi.items),
            stops: this.getStops(route.kpi.stops),
            tasks: this.getTasks(route)
        } as KPI
    }

    getTasks(route: Routes): Task {
        const routeState = route.route_state;
        const task = route.kpi.tasks;
        const completedTask = task.completed??0
        const totalTask = task.total??0
        return ({
            completed: task.completed,
            deliver: task.deliver,
            emptymailbox: task.emptymailbox,
            handover: task.handover,
            loaded: task.loaded,
            pickup: task.pickup,
            planned: task.planned,
            tasks_left: this.getTaskLeftCount(task, routeState),
            completedPercentage: totalTask ? Number(((completedTask/totalTask)*100).toFixed(2)) : 0,
            tasks_pr_hour: task.tasks_pr_hour,
            total: task.total,
            visit: task.visit,
            task_workload_expected: task.task_workload_expected,
            task_workload_variance:task.task_workload_variance

            
        })
    }

    getTaskLeftCount(task: Task, route_state: string) {
        if (route_state?.toLowerCase() === RouteState.Onroute.toLowerCase() || route_state?.toLowerCase() === RouteState.Completed.toLowerCase()) {
            return (task.total - task.completed).toString();
        } else return 'NA';
    }

    getStops(stop: Stop): Stop {
        return ({
            completed: stop.completed,
            loaded: stop.loaded,
            total: stop.total
        })
    }

    getItems(item: Item): Item {
        return ({
            delivered: item.delivered,
            failedattempt: item.failedattempt,
            letter: item.letter,
            loaded: item.loaded,
            parcel: item.parcel,
            pallet: item.pallet,
            pickup: item.pickup,
            planned: item.planned,
            planned_at_loading: item.planned_at_loading,
            items_moved_in: item.items_moved_in,
            items_moved_out: item.items_moved_out,
            total: item.total,
            volume: item.volume,
            weight: item.weight,
            planned_at_sorting: item.planned_at_sorting,
            non_pickups: item.non_pickups,
            total_loaded: item.total_loaded,
            notified: item.notified,
            pickup_completed: item.pickup_completed,
            pickup_failed: item.pickup_failed,
            signature_required: item.signature_required
        });
    }

    //processing the route info object

    sort<T>(input: T[], prop: string) {
        return this.dataFormatService.sortByProperty(input, prop);
    }

    setRouteMap(routes: Routes[]) {
        routes.forEach(route => {
            this.dataRepositoryService.kpiDataMap.set(route.route_id, route);
        })
    }

    //delta implementation 

    deltaAdaptArray(routeInfoResponse: RouteInfoResp): RouteInfoResp {
        (routeInfoResponse.route_info ?? []).forEach((rI: RouteInfo) => {
            this.updateRoutes(rI.routes)
        })
        return routeInfoResponse;
    }

    updateRoutes(routeList: Routes[]) {
        routeList.forEach((route: Routes, index) => {
            const kpi =this.getKPI(route);
            route.kpi={
                ...route.kpi,
                ...kpi
            };
            let routeObj: Routes = {
                kpi_extended:route.kpi_extended,
                kpi,
                route_id: route.route_id,
                route_label: route.route_label,
                route_schedule: route.route_schedule,
                route_state: route.route_state,
                route_time_window: route.route_time_window,
                failed_tasks: route.failed_tasks,
                workshift_name: route?.workshift_name??'',
                vehicle: route.vehicle??'',
                driver_information:route.driver_information,
                break_time_window:route?.break_time_window,
                finishloading_ts: route?.finishloading_ts,
                loading_ts: route?.loading_ts,
                onroute_ts: route?.onroute_ts,
                canCompleteRoute: this.canMarkRouteComplete(route),
                container_information : route?.container_information??{},
                enable_slotin: route?.enable_slotin??false
            };
            this.dataRepositoryService.kpiDataMap.set(route.route_id, routeObj);
        })
        const sortRoute = new Map([...this.dataRepositoryService.kpiDataMap.entries()].sort((a, b) => a[1].route_label.localeCompare(b[1].route_label)));
        this.dataRepositoryService.kpiDataMap = sortRoute;
        //for reference change, use temp assignment, so that ngOnChange hook can be triggered
        const newRouteMap = new Map(this.dataRepositoryService.kpiDataMap.entries());
        this.dataRepositoryService.kpiDataMap = newRouteMap;
    }

    /**
     * This method checks if route is eligible to mark as complete.
     * @param route route details
     * @returns true if all tasks are complete, else false.s
     */
    canMarkRouteComplete(route: Routes) {
        return route.route_state.toLowerCase() === RouteState.Onroute.toLowerCase() && route.kpi.tasks.total === route.kpi.tasks.completed      
    }
}